/**
 * shared/playerstate.ts — PlayerState factory, schema, copy/fround helpers, predictable events.
 *
 * OWNER: integration (contract). The schema below is THE single source for the PlayerState
 * codec (netcode builds encode/decode from it) and for clone/compare in prediction tests.
 * Adding a field to PlayerState = add it to createPlayerState() AND PS_SCHEMA (a unit test
 * enforces they match). Request via docs/CONTRACT-REQUESTS.md.
 */
import {
  G_GRAVITY, G_SPEED, PLAYER_MAX_HEALTH, SNS_SPRINT_COOLDOWN_MS, SNS_SPRINT_DURATION_MS,
  VIEWHEIGHT_STAND, ENTITYNUM_NONE,
} from './constants';
import {
  MAX_PS_EVENTS, NUM_WEAPONS, PmType, Stance, WeaponId, WeaponState, PMF,
  type PlayerState, type PEv,
} from './types';

export type PsFieldType = 'f32' | 'i32' | 'u32' | 'i16' | 'u16' | 'u8';
/** [field name, wire type, element count (1 = scalar, >1 = fixed array/Vec3)] */
export type PsFieldSpec = readonly [keyof PlayerState, PsFieldType, number];

export const PS_SCHEMA: readonly PsFieldSpec[] = [
  ['clientNum', 'u8', 1],
  ['commandTime', 'i32', 1],
  ['pm_type', 'u8', 1],
  ['pm_flags', 'u32', 1],
  ['pm_time', 'i32', 1],
  ['eFlags', 'u32', 1],
  ['spawnCount', 'u8', 1],
  ['oldButtons', 'u32', 1],
  ['origin', 'f32', 3],
  ['velocity', 'f32', 3],
  ['viewangles', 'f32', 3],
  ['deltaAngles', 'i16', 3],
  ['gravity', 'i32', 1],
  ['speed', 'i32', 1],
  ['moveSpeedScaleMultiplier', 'f32', 1],
  ['leanf', 'f32', 1],
  ['groundEntityNum', 'u16', 1],
  ['vLadderVec', 'f32', 3],
  ['jumpTime', 'i32', 1],
  ['jumpOriginZ', 'f32', 1],
  ['damageTimer', 'i32', 1],
  ['waterlevel', 'u8', 1],
  ['movementDir', 'u8', 1],
  ['bobCycle', 'u8', 1],
  ['aimSpreadScale', 'f32', 1],
  ['viewHeightTarget', 'i32', 1],
  ['viewHeightCurrent', 'f32', 1],
  ['viewHeightLerpTime', 'i32', 1],
  ['viewHeightLerpTarget', 'i32', 1],
  ['viewHeightLerpDown', 'u8', 1],
  ['proneDirection', 'f32', 1],
  ['proneDirectionPitch', 'f32', 1],
  ['proneTorsoPitch', 'f32', 1],
  ['sprintButtonUpRequired', 'u8', 1],
  ['sprintDelay', 'u8', 1],
  ['lastSprintStart', 'i32', 1],
  ['lastSprintEnd', 'i32', 1],
  ['sprintStartMaxLength', 'i32', 1],
  ['sprintDurationBase', 'i32', 1],
  ['sprintCooldown', 'i32', 1],
  ['mantleYaw', 'f32', 1],
  ['mantleTimer', 'i32', 1],
  ['mantleTransIndex', 'u8', 1],
  ['mantleFlags', 'u8', 1],
  ['dtpState', 'u8', 1],
  ['dtpTime', 'i32', 1],
  ['dtpYaw', 'f32', 1],
  ['meleeChargeYaw', 'f32', 1],
  ['meleeChargeDist', 'u8', 1],
  ['meleeChargeTime', 'i32', 1],
  ['weapon', 'u8', 1],
  ['weaponState', 'u8', 1],
  ['weaponTime', 'i32', 1],
  ['weaponDelay', 'i32', 1],
  ['weaponAnim', 'u16', 1],
  ['weaponPending', 'u8', 1],
  ['offHandIndex', 'u8', 1],
  ['offhandPrimary', 'u8', 1],
  ['meleeWeapon', 'u8', 1],
  ['grenadeTimeLeft', 'i32', 1],
  ['weaponsOwned', 'u32', 1],
  ['ammoClip', 'u8', NUM_WEAPONS],
  ['ammoStock', 'u8', NUM_WEAPONS],
  ['adsFrac', 'f32', 1],
  ['adsDelayTime', 'i32', 1],
  ['fireSeq', 'u16', 1],
  ['weaponShotCount', 'u8', 1], // weapons #W1
  ['weaponsHeldBefore', 'u8', 1], // weapons #W1
  ['fireSpreadScale', 'u8', 1], // weapons #W1
  ['health', 'i16', 1],
  ['maxHealth', 'u8', 1],
  ['eventSequence', 'u16', 1],
  ['events', 'u8', MAX_PS_EVENTS],
  ['eventParms', 'u16', MAX_PS_EVENTS],
];

export function createPlayerState(clientNum = 0): PlayerState {
  return {
    clientNum,
    commandTime: 0,
    pm_type: PmType.NORMAL,
    pm_flags: 0,
    pm_time: 0,
    eFlags: 0,
    spawnCount: 0,
    oldButtons: 0,
    origin: [0, 0, 0],
    velocity: [0, 0, 0],
    viewangles: [0, 0, 0],
    deltaAngles: [0, 0, 0],
    gravity: G_GRAVITY,
    speed: G_SPEED,
    moveSpeedScaleMultiplier: 1,
    leanf: 0,
    groundEntityNum: ENTITYNUM_NONE,
    vLadderVec: [0, 0, 0],
    jumpTime: 0,
    jumpOriginZ: 0,
    damageTimer: 0,
    waterlevel: 0,
    movementDir: 0,
    bobCycle: 0,
    aimSpreadScale: 0,
    viewHeightTarget: VIEWHEIGHT_STAND,
    viewHeightCurrent: VIEWHEIGHT_STAND,
    viewHeightLerpTime: 0,
    viewHeightLerpTarget: 0,
    viewHeightLerpDown: 0,
    proneDirection: 0,
    proneDirectionPitch: 0,
    proneTorsoPitch: 0,
    sprintButtonUpRequired: 0,
    sprintDelay: 0,
    lastSprintStart: 0,
    lastSprintEnd: 0,
    sprintStartMaxLength: 0,
    sprintDurationBase: SNS_SPRINT_DURATION_MS,
    sprintCooldown: SNS_SPRINT_COOLDOWN_MS,
    mantleYaw: 0,
    mantleTimer: 0,
    mantleTransIndex: 0,
    mantleFlags: 0,
    dtpState: 0,
    dtpTime: 0,
    dtpYaw: 0,
    meleeChargeYaw: 0,
    meleeChargeDist: 0,
    meleeChargeTime: 0,
    weapon: WeaponId.NONE,
    weaponState: WeaponState.READY,
    weaponTime: 0,
    weaponDelay: 0,
    weaponAnim: 0,
    weaponPending: WeaponId.NONE,
    offHandIndex: WeaponId.NONE,
    offhandPrimary: WeaponId.NONE,
    meleeWeapon: WeaponId.NONE,
    grenadeTimeLeft: 0,
    weaponsOwned: 0,
    ammoClip: new Array<number>(NUM_WEAPONS).fill(0),
    ammoStock: new Array<number>(NUM_WEAPONS).fill(0),
    adsFrac: 0,
    adsDelayTime: 0,
    fireSeq: 0,
    weaponShotCount: 0,
    weaponsHeldBefore: 0,
    fireSpreadScale: 0,
    health: PLAYER_MAX_HEALTH,
    maxHealth: PLAYER_MAX_HEALTH,
    eventSequence: 0,
    events: new Array<number>(MAX_PS_EVENTS).fill(0),
    eventParms: new Array<number>(MAX_PS_EVENTS).fill(0),
  };
}

/** Deep copy src into dst (no allocation). Schema-driven so it can never miss a field. */
export function copyPlayerState(dst: PlayerState, src: PlayerState): PlayerState {
  const d = dst as unknown as Record<string, number | number[]>;
  const s = src as unknown as Record<string, number | number[]>;
  for (const [name, , count] of PS_SCHEMA) {
    if (count === 1) d[name] = s[name] as number;
    else {
      const da = d[name] as number[], sa = s[name] as number[];
      for (let i = 0; i < count; i++) da[i] = sa[i];
    }
  }
  return dst;
}

export function clonePlayerState(src: PlayerState): PlayerState {
  return copyPlayerState(createPlayerState(src.clientNum), src);
}

/**
 * Quantize every field to its wire type (f32 -> Math.fround, ints -> wrap). Call at the end of
 * every pmove step on BOTH server and client so server state == decoded wire state == replayed
 * client state, bit for bit [NET §6.3].
 */
export function quantizePlayerState(ps: PlayerState): PlayerState {
  const p = ps as unknown as Record<string, number | number[]>;
  for (const [name, type, count] of PS_SCHEMA) {
    if (count === 1) p[name] = quant(type, p[name] as number);
    else {
      const a = p[name] as number[];
      for (let i = 0; i < count; i++) a[i] = quant(type, a[i]);
    }
  }
  return ps;
}
function quant(type: PsFieldType, v: number): number {
  switch (type) {
    case 'f32': return Math.fround(v);
    case 'i32': return v | 0;
    case 'u32': return v >>> 0;
    case 'i16': return (v << 16) >> 16;
    case 'u16': return v & 0xffff;
    case 'u8': return v & 0xff;
  }
}

/** Field-by-field comparison; returns the first differing field name or null (tests, desync debug). */
export function diffPlayerState(a: PlayerState, b: PlayerState): string | null {
  const pa = a as unknown as Record<string, number | number[]>;
  const pb = b as unknown as Record<string, number | number[]>;
  for (const [name, , count] of PS_SCHEMA) {
    if (count === 1) { if (!Object.is(pa[name], pb[name])) return name; }
    else {
      const x = pa[name] as number[], y = pb[name] as number[];
      for (let i = 0; i < count; i++) if (!Object.is(x[i], y[i])) return `${name}[${i}]`;
    }
  }
  return null;
}

/** Q3 BG_AddPredictableEventToPlayerstate: push into the ps event ring. */
export function addPredictableEvent(ps: PlayerState, ev: PEv, parm: number): void {
  const slot = ps.eventSequence & (MAX_PS_EVENTS - 1);
  ps.events[slot] = ev;
  ps.eventParms[slot] = parm & 0xffff;
  ps.eventSequence = (ps.eventSequence + 1) & 0xffff;
}

/**
 * Iterate events with sequence in [fromSeq, ps.eventSequence) that are still in the ring
 * (older ones were overwritten; at 60 Hz with MAX_PS_EVENTS=4 per cmd this is fine).
 */
export function forEachNewEvent(ps: PlayerState, fromSeq: number, fn: (ev: PEv, parm: number, seq: number) => void): void {
  let start = fromSeq;
  const end = ps.eventSequence;
  const count = (end - start) & 0xffff;
  if (count > MAX_PS_EVENTS) start = (end - MAX_PS_EVENTS) & 0xffff;
  for (let s = start; s !== end; s = (s + 1) & 0xffff) {
    const slot = s & (MAX_PS_EVENTS - 1);
    fn(ps.events[slot] as PEv, ps.eventParms[slot], s);
  }
}

/** Gameplay stance from pm_flags. */
export function psStance(ps: PlayerState): Stance {
  if (ps.pm_flags & PMF.PRONE) return Stance.PRONE;
  if (ps.pm_flags & PMF.DUCKED) return Stance.CROUCH;
  return Stance.STAND;
}

/** Eye position (CoD space): origin + (0,0,viewHeightCurrent). */
export function psEyePos(ps: PlayerState, out: [number, number, number]): [number, number, number] {
  out[0] = ps.origin[0]; out[1] = ps.origin[1]; out[2] = ps.origin[2] + ps.viewHeightCurrent;
  return out;
}

export function ownsWeapon(ps: PlayerState, w: WeaponId): boolean {
  return (ps.weaponsOwned & (1 << w)) !== 0;
}
